/**
 * Pure calculation helpers for the Map Dashboard zone panel and movement vectors.
 * No React / alias imports so they can be unit-tested in isolation.
 */

export interface HistorySample {
  zoneId: string;
  sampledAt: string;
  capacityUtilisation: number;
}

export interface FlowFields {
  dominantDirection?: number | null;
  entryRate?: number | null;
  exitRate?: number | null;
  netFlow?: number | null;
}

export const SURGE_RATE_LOOKBACK_MS = 5 * 60_000;
export const SURGE_RATE_LOOKBACK_TOLERANCE_MS = 2 * 60_000;
export const MOVEMENT_VECTOR_BASE_DEGREES = 0.00016;
export const MOVEMENT_VECTOR_MAX_FLOW_SHARE = 0.15;
export const MOVEMENT_VECTOR_FLOW_SHARE_SCALE = 0.0013;

/** Real utilisation samples for one zone, oldest first. */
export function buildDensitySparkline(history: HistorySample[], zoneId: string): number[] {
  return history
    .filter(point => point.zoneId === zoneId && Number.isFinite(new Date(point.sampledAt).getTime()))
    .sort((a, b) => new Date(a.sampledAt).getTime() - new Date(b.sampledAt).getTime())
    .map(point => point.capacityUtilisation);
}

/** Closest history sample to targetMs, or null if nothing falls within toleranceMs. */
export function utilisationNear(history: HistorySample[], targetMs: number, toleranceMs: number): number | null {
  let best: HistorySample | null = null;
  let bestDistanceMs = Infinity;
  for (const point of history) {
    const sampledMs = new Date(point.sampledAt).getTime();
    if (!Number.isFinite(sampledMs)) continue;
    const distanceMs = Math.abs(sampledMs - targetMs);
    if (distanceMs < bestDistanceMs) {
      bestDistanceMs = distanceMs;
      best = point;
    }
  }
  return best && bestDistanceMs <= toleranceMs ? best.capacityUtilisation : null;
}

/** Signed change in utilisation (percentage points) vs the sample nearest now-5min, or null. */
export function computeSurgeRate5Min(
  currentUtilisation: number,
  zoneHistory: HistorySample[],
  nowMs: number,
): number | null {
  const past = utilisationNear(zoneHistory, nowMs - SURGE_RATE_LOOKBACK_MS, SURGE_RATE_LOOKBACK_TOLERANCE_MS);
  return past === null ? null : currentUtilisation - past;
}

/** Compass-bearing flow offset in [dLat, dLng] degrees, or null when no real bearing exists. */
export function computeMovementVectorOffset(
  flow: FlowFields | undefined,
  capacity: number,
): [number, number] | null {
  if (!flow) return null;
  const bearing = flow.dominantDirection;
  if (bearing === null || bearing === undefined || !Number.isFinite(bearing)) return null;

  const entry = flow.entryRate ?? 0;
  const exit = flow.exitRate ?? 0;
  const volume = Math.abs(entry) + Math.abs(exit);
  const net = Math.abs(flow.netFlow ?? (entry - exit));
  const magnitude = Math.max(volume, net);
  const flowShare = capacity > 0 ? Math.min(magnitude / capacity, MOVEMENT_VECTOR_MAX_FLOW_SHARE) : 0;
  const distance = MOVEMENT_VECTOR_BASE_DEGREES + flowShare * MOVEMENT_VECTOR_FLOW_SHARE_SCALE;
  const rad = (bearing * Math.PI) / 180;
  return [distance * Math.cos(rad), distance * Math.sin(rad)];
}
